import { SheetDescription, SheetHeader, SheetTitle } from "design-system/atoms/Sheet"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "design-system/organisms/Form"
import { ensure } from "lopermut"
import { useCapabilitiesQuery } from "modules/capabilities/hooks/useCapabilitiesQuery"
import { useGoalsQuery } from "modules/campaigns/hooks/sections/goals/useGoalsQuery"
import { useMemo } from "react"
import { type FieldPath } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Fragment } from "react/jsx-runtime"
import { WorkflowConditionFieldEnum, WorkflowEventEnum } from "schema"
import { useWorkflowBuilder } from "../hooks/useWorkflowBuilder"
import { EventIconMap } from "../maps/eventIconMap"
import { BuilderFormValues } from "../lib/workflowsCanvas"

const AllEvents = Object.values(WorkflowEventEnum)

export function EventSheet() {
  const { t } = useTranslation()
  const { form, focusedNode } = useWorkflowBuilder()

  const nodeIndex = useMemo(
    () => ensure(form.watch("nodes")?.findIndex((node) => node.id === focusedNode?.id)),
    [form, focusedNode],
  )

  const selectedEvent = form.watch(`nodes.${nodeIndex}.event`)

  const isGoalAchievedEvent = selectedEvent === WorkflowEventEnum.ConversationGoalAchieved
  const isCapabilityEvent =
    selectedEvent === WorkflowEventEnum.CapabilityEvaluated ||
    selectedEvent === WorkflowEventEnum.CapabilityExecuted

  const { data: goals } = useGoalsQuery({
    query: { total: 100 },
    enabled: isGoalAchievedEvent,
  })

  const { data: capabilities } = useCapabilitiesQuery({ enabled: isCapabilityEvent })

  if (nodeIndex < 0) return null

  return (
    <SheetHeader className="flex flex-col gap-4 text-left">
      <div className="flex flex-col gap-2">
        <SheetTitle>{t("campaigns.event.label")}</SheetTitle>
        <SheetDescription>{t("campaigns.event.title")}</SheetDescription>
      </div>
      <div className="flex flex-col gap-4">
        <FormField
          control={form.control}
          name={`nodes.${nodeIndex}.event`}
          render={({ field }) => {
            const selectedEventLabel = selectedEvent
              ? t(`campaigns.event.options.${selectedEvent}.label`)
              : t("campaigns.event.placeholder")
            const SelectedEventIcon = selectedEvent ? EventIconMap[selectedEvent] : null

            return (
              <FormItem>
                <FormLabel>{t("campaigns.event.label")}</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value, { shouldDirty: true })
                      if (value !== WorkflowEventEnum.CapabilityExecuted) {
                        const nodes = form.getValues("nodes")
                        nodes?.forEach((node, idx) => {
                          if (node.type !== "Condition") return
                          const conditionNode = node as Extract<
                            (typeof nodes)[number],
                            { type: "Condition" }
                          >
                          const filtered = conditionNode.conditions.filter(
                            (c) => c.field !== WorkflowConditionFieldEnum.CapabilityExecutionStatus,
                          )
                          if (filtered.length !== conditionNode.conditions.length) {
                            form.setValue(
                              `nodes.${idx}.conditions` as FieldPath<BuilderFormValues>,
                              filtered as never,
                              { shouldDirty: true },
                            )
                          }
                        })
                      }
                      form.trigger()
                    }}
                    value={selectedEvent}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("campaigns.event.placeholder")}>
                        <div className="flex items-center gap-2">
                          {selectedEvent && SelectedEventIcon ? (
                            <div className="flex items-center gap-2">
                              <SelectedEventIcon className="size-5" />
                              <span className="font-medium">{selectedEventLabel}</span>
                            </div>
                          ) : (
                            t("campaigns.event.placeholder")
                          )}
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {AllEvents.map((event, index) => {
                        const Icon = EventIconMap[event]

                        return (
                          <Fragment key={event}>
                            <SelectItem value={event}>
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <Icon className="size-5" />
                                  <span className="font-medium">
                                    {t(`campaigns.event.options.${event}.label`)}
                                  </span>
                                </div>
                                <span className="text-sm text-gray-500">
                                  {t(`campaigns.event.options.${event}.description`)}
                                </span>
                              </div>
                            </SelectItem>
                            {index < AllEvents.length - 1 && <SelectSeparator />}
                          </Fragment>
                        )
                      })}
                    </SelectContent>
                  </Select>
                </FormControl>
                {form.watch(`nodes.${nodeIndex}.event`) && (
                  <FormDescription className="text-sm text-muted-foreground">
                    {t(
                      `campaigns.event.options.${form.watch(`nodes.${nodeIndex}.event`)}.description`,
                    )}
                  </FormDescription>
                )}
              </FormItem>
            )
          }}
        />
        {selectedEvent === WorkflowEventEnum.ConversationGoalAchieved && (
          <div className="flex flex-col gap-2">
            <FormField
              control={form.control}
              name={`nodes.${nodeIndex}.config.goal`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("campaigns.event.goals.label")}</FormLabel>
                  <FormControl>
                    <Select
                      value={form.watch(`nodes.${nodeIndex}.config.goal`)}
                      onValueChange={(value) => {
                        field.onChange(value, { shouldDirty: true })
                        form.trigger()
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("campaigns.event.goals.placeholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {goals?.map((goal) => (
                          <SelectItem key={goal.id} value={goal.id}>
                            {goal.displayName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormDescription className="text-sm text-muted-foreground">
                    {t("campaigns.event.goals.description")}
                  </FormDescription>
                </FormItem>
              )}
            />
          </div>
        )}
        {isCapabilityEvent && (
          <div className="flex flex-col gap-2">
            <FormField
              control={form.control}
              name={`nodes.${nodeIndex}.config.capabilityId`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("campaigns.event.capability.label")}</FormLabel>
                  <FormControl>
                    <Select
                      value={form.watch(`nodes.${nodeIndex}.config.capabilityId`)}
                      onValueChange={(value) => {
                        field.onChange(value, { shouldDirty: true })
                        form.trigger()
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("campaigns.event.capability.placeholder")} />
                      </SelectTrigger>
                      <SelectContent>
                        {capabilities?.map((capability) => (
                          <SelectItem key={capability.id} value={capability.id}>
                            {capability.name ?? capability.identifier}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormDescription className="text-sm text-muted-foreground">
                    {selectedEvent === WorkflowEventEnum.CapabilityEvaluated
                      ? t("campaigns.event.capability.evaluatedDescription")
                      : t("campaigns.event.capability.executedDescription")}
                  </FormDescription>
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
    </SheetHeader>
  )
}
